/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { NodeKey } from "lexical"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection"
import { mergeRegister } from "@lexical/utils"
import { useCallback, useEffect, useRef } from "react"
import { $isInlineImageNode } from "./InlineImageNode"
import { ImageSettingsForm } from "./FloatingImageEditor/FloatingImageEditor"
import { EditableImage } from "./EditableImage/EditableImage"
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
} from "lexical"

export default function InlineImageComponent({
  src,
  nodeKey,
  link,
  width,
  isRounded,
  fileName,
  fileType,
  topText,
  topTextColor,
  bottomText,
  bottomTextColor,

  onImageSettingsChange,
}: {
  nodeKey: NodeKey
  src: string
  link?: string
  width?: number
  isRounded?: boolean
  fileName?: string
  fileType?: string
  topText?: string
  topTextColor?: string
  bottomText?: string
  bottomTextColor?: string

  onImageSettingsChange?: (settings: ImageSettingsForm) => void
}): JSX.Element {
  const imageRef = useRef<null | HTMLImageElement>(null)
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey)
  const [editor] = useLexicalComposerContext()

  const handleDelete = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload
        event.preventDefault()
        const node = $getNodeByKey(nodeKey)
        if ($isInlineImageNode(node)) {
          node.selectPrevious()
          node.remove()
        }
      }
      return false
    },
    [isSelected, nodeKey]
  )

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === imageRef.current) {
        clearSelection()
        setSelected(true)

        return true
      }

      return false
    },
    [clearSelection, setSelected]
  )

  useEffect(
    function registerEditorListeners() {
      const unregister = mergeRegister(
        editor.registerCommand(CLICK_COMMAND, handleClick, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_BACKSPACE_COMMAND, handleDelete, COMMAND_PRIORITY_LOW)
      )

      return () => {
        unregister()
      }
    },
    [clearSelection, editor, isSelected, nodeKey, handleDelete, setSelected, handleClick]
  )

  const handleImageSettingsChange = useCallback(
    ({ isRounded, link, width, topText, topTextColor, bottomText, bottomTextColor }: ImageSettingsForm) => {
      editor.update(() => {
        onImageSettingsChange?.({ isRounded, link, width, topText, topTextColor, bottomText, bottomTextColor })
      })
    },
    [editor, onImageSettingsChange]
  )

  return (
    <EditableImage
      className={isSelected ? `outline outline-2 outline-sky-500` : null}
      src={src}
      imageRef={imageRef}
      link={link}
      width={width}
      fileName={fileName}
      fileType={fileType}
      topText={topText}
      topTextColor={topTextColor}
      bottomText={bottomText}
      bottomTextColor={bottomTextColor}
      isRounded={isRounded}
      onImageSettingsChange={handleImageSettingsChange}
    />
  )
}
